import { useCallback } from "react";
import { fetchWithNetworkLogging } from "@Front/Api/helpers/requestHelpers";
import type { ApiError } from "@Front/Hooks/useApiClientTypes";
import type { ApiResponse } from "@Front/Hooks/useApiClientTypes";
import { parseResponseData } from "@Front/Hooks/useApiClientRequestCore";

type ExecuteRequestParams = {
	url: string;
	init: RequestInit;
	method: string;
	endpoint: string;
	errorMessage?: string;
	logErrors?: boolean;
	background?: boolean;
};

export function useApiExecuteRequest(
	handleApiError: (response: Response, errorMessage?: string, logErrors?: boolean) => Promise<ApiError>,
): <T = unknown>(params: ExecuteRequestParams) => Promise<ApiResponse<T>> {
	return useCallback(
		async <T = unknown>(params: ExecuteRequestParams): Promise<ApiResponse<T>> => {
			const { url, init, errorMessage, logErrors, background } = params;
			const response = await fetchWithNetworkLogging(url, init, background ? { background: true } : undefined);

			if (!response.ok) {
				throw await handleApiError(response, errorMessage, logErrors);
			}

			const data = await parseResponseData<T>(response);
			return {
				data,
				status: response.status,
				statusText: response.statusText,
				ok: response.ok,
			};
		},
		[handleApiError],
	);
}
